import { useMemo } from "react";

import type { EventContext, Event } from "types/klaviyo";

import { useSite } from "./useSite";

/**
 * Safely push events to Klaviyo (window.klaviyo).
 * Klaviyo is included as an ordinary script via gatsby-ssr.tsx.
 * @see https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object
 */
export function useKlaviyo() {
  const site = useSite();

  const eventContext = useMemo<EventContext>(
    () => ({
      site_handle: site.handle,
      currency: site.currencyCode,
    }),
    [site],
  );

  return useMemo(
    () => ({
      identify: (properties: Record<string, any>) => {
        klaviyo()?.identify(properties);
      },
      track: <T extends Event["event"]>(
        event: T,
        properties: Extract<Event, { event: T }>["properties"],
      ) => {
        klaviyo()?.track(event, {
          ...eventContext,
          ...properties,
        });
      },
    }),
    [],
  );
}

/**
 * Safely get Klaviyo from the window.
 */
function klaviyo() {
  if ("undefined" === typeof window) return;

  const klaviyo = window.klaviyo;
  if (!klaviyo) return;

  return window.klaviyo;
}

declare global {
  interface Window {
    klaviyo?: {
      identify: (properties: Record<string, any>) => void;
      track: (event: string, properties?: Record<string, any>) => void;
    };
  }
}
